import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import {
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Vector3 } from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import Easing from '../../Common/Utils/Math/Easing';
import useAnimFrame from '../../Common/_hooks/useAnimFrame';
import Lights from '../Presentation/Lights';
import {
    CAMERA_MAX_AZIMUTH_DEFAULT,
    CAMERA_MAX_DISTANCE_DEFAULT,
    CAMERA_MAX_POLAR_DEFAULT,
    CAMERA_MIN_AZIMUTH_DEFAULT,
    CAMERA_MIN_DISTANCE_DEFAULT,
    CAMERA_MIN_POLAR_DEFAULT,
    CAMERA_PAN_LIMIT_RATIO,
    FIXED_VIEW_TRANSITION_TIME_MS,
} from './settings';
import useFixedView from './useFixedView';
import useFixedViewTime from './useFixedViewTime';
import type { LabData } from '../../AppData/LabData';
import {
    DEFAULT_MODEL_VIEWER_CAMERA_FOV,
    MODEL_VIEWER_PRESENTATION_SETUPS,
} from '../Presentation/Setup';
import type { CameraView } from './types';
import type { ModelViewerPreviewData } from '../previewData';
import RenderingContext from '../Scene/RenderingContext';
import { renderReadiness } from '../Rendering/browserReadiness';

const ENTRY_ANIMATION_TIME_MS = 1000
const CAMERA_SYNC_EPSILON_SQUARED = 0.0000000001
const CAMERA_SYNC_STABLE_FRAMES = 2
const CAMERA_SYNC_MAX_FRAMES = 120

function getEntryCameraPosition(
    defaultPosition: Vector3,
    entryPosition: Vector3,
    startedAt: number,
) {
    const progress = Easing.EaseOutQuart(Math.min(
        1,
        Math.max(0, (Date.now() - startedAt) / ENTRY_ANIMATION_TIME_MS),
    ))

    return defaultPosition.clone().setZ(
        entryPosition.z + progress * (defaultPosition.z - entryPosition.z),
    )
}

interface Props {
    labData: LabData
    model?: string
    variant: string
    cameraDistanceScale?: number
    cameraCommandId?: number
    cameraPanEnabled?: boolean
    cameraRevision?: number
    entryAnimationStartedAt?: number
    deferDefaultCamera?: boolean
    previewData: ModelViewerPreviewData
    onControlsUsed?: () => void
    onCameraChange?: (view: CameraView) => void
}

/**
 * Owns camera and OrbitControls state. A zero-vector request selects the
 * product-configured default; changing FOV rescales distance to retain framing.
 * Pedestal defaults wait for measured model bounds, then move the camera back
 * without changing explicit coordinates copied from the interactive viewer.
 * Camera revisions are acknowledged only after controls and the demand renderer
 * have consumed the requested position and target.
 *
 * Panning is opt-in because this rig is also used by the Design Lab. The target
 * is kept within a product-relative sphere, and the camera receives the same
 * correction so reaching that boundary does not change framing.
 */
export default function CameraRig(props: Props) {
    const {camera, presentation} = props.previewData
    const {viewerMode} = useContext(RenderingContext)
    const websiteMode = viewerMode === 'website'
    const interactive = viewerMode !== 'render'
    const presentationSetup = presentation.setup
    const presentationConfig = MODEL_VIEWER_PRESENTATION_SETUPS[presentationSetup]
    const cameraFov = camera.fov
    const cameraDistanceScale = Math.max(1, props.cameraDistanceScale ?? 1)
    const fovDistanceScale = Math.tan(DEFAULT_MODEL_VIEWER_CAMERA_FOV * Math.PI / 360)
        / Math.tan(cameraFov * Math.PI / 360)
    const invalidate = useThree((state) => state.invalidate)
    const gl = useThree((state) => state.gl)
    const activeRenderCamera = useThree((state) => state.camera)

    const controls = useRef<OrbitControlsImpl>(null)
    const [fixedView, setFixedView] = useFixedView()
    const [fixedViewTime, setFixedViewTime] = useFixedViewTime()
    const fixedViewRequest = useRef<{
        view: typeof fixedView
        startedAt: number
        polar: number
        azimuthal: number
    } | null>(null)
    const inFixedViewTransition = Date.now() < fixedViewTime + FIXED_VIEW_TRANSITION_TIME_MS && fixedView !== 'custom'
    
    // A named model can override camera limits in its Lab JSON. Fall back to
    // the product-level configuration for single-model products.
    const modelViewerData = useMemo(() => {
        return props.model
            ? props.labData.variants[props.variant].models?.[props.model] ?? props.labData
            : props.labData;
    }, [props.labData, props.model, props.variant])

    const baseMinDistance = useMemo(() => (
        modelViewerData.minDistance ?? CAMERA_MIN_DISTANCE_DEFAULT
    ), [modelViewerData])
    const baseMaxDistance = useMemo(() => (
        modelViewerData.maxDistance ?? CAMERA_MAX_DISTANCE_DEFAULT
    ), [modelViewerData])
    const minDistance = baseMinDistance
        * presentationConfig.cameraDistanceScale
        * Math.min(1, fovDistanceScale)
    const maxDistance = baseMaxDistance
        * presentationConfig.cameraDistanceScale
        * Math.max(1, fovDistanceScale)
        * cameraDistanceScale
    const defaultDistance = baseMaxDistance
        * presentationConfig.cameraDistanceScale
        * fovDistanceScale
        * cameraDistanceScale
    const maxPanDistance = baseMaxDistance
        * presentationConfig.cameraDistanceScale
        * cameraDistanceScale
        * CAMERA_PAN_LIMIT_RATIO
    const minPolar = useMemo(() => modelViewerData.minPolar ?? CAMERA_MIN_POLAR_DEFAULT, [modelViewerData])
    const maxPolar = useMemo(() => modelViewerData.maxPolar ?? CAMERA_MAX_POLAR_DEFAULT, [modelViewerData])
    const minAzimuth = useMemo(() => modelViewerData.minAzimuth ?? CAMERA_MIN_AZIMUTH_DEFAULT, [modelViewerData])
    const maxAzimuth = useMemo(() => modelViewerData.maxAzimuth ?? CAMERA_MAX_AZIMUTH_DEFAULT, [modelViewerData])

    // TODO: This should just default to true
    const directionalShadows = useMemo(
        () => modelViewerData.directionalShadows ?? true,
        [modelViewerData],
    )

    const initPosition = useMemo(() => {
        return new Vector3(
            modelViewerData.initPosition?.x ?? 0,
            modelViewerData.initPosition?.y ?? 0,
            modelViewerData.initPosition?.z ?? (baseMinDistance + baseMaxDistance) / 2
        ).multiplyScalar(presentationConfig.cameraDistanceScale)
    }, [
        baseMaxDistance,
        baseMinDistance,
        modelViewerData,
        presentationConfig.cameraDistanceScale,
    ])

    const defaultCameraPosition = useMemo(() => {
        return websiteMode
            ? initPosition.clone()
            : initPosition.clone().setZ(defaultDistance)
    }, [defaultDistance, initPosition, websiteMode])
    const entryCameraPosition = useMemo(() => {
        const direction = defaultCameraPosition.z >= 0 ? 1 : -1
        return defaultCameraPosition.clone().setZ(maxDistance * direction)
    }, [defaultCameraPosition, maxDistance])
    const requestedCameraPosition = useRef(camera.position)
    requestedCameraPosition.current = camera.position
    const requestedCameraTarget = useRef(camera.target)
    requestedCameraTarget.current = camera.target
    const hasRequestedCameraPosition = (
        camera.position.x !== 0
        || camera.position.y !== 0
        || camera.position.z !== 0
    )

    // Studio lights rotate from this direction so front/back previews retain
    // the same key/fill relationship as the camera orbits the product.
    const [cameraDirection, setCameraDirection] = useState(new Vector3())
    
    const entryTime = props.entryAnimationStartedAt
    const inEntryAnimation = (
        websiteMode
        && !hasRequestedCameraPosition
        && entryTime !== undefined
        && Date.now() < entryTime + ENTRY_ANIMATION_TIME_MS
    )
    const [cameraPosition, setCameraPosition] = useState(
        inEntryAnimation
            ? getEntryCameraPosition(defaultCameraPosition, entryCameraPosition, entryTime)
            : defaultCameraPosition,
    )

    // @ts-expect-error Temporary browser-console positioning helper.
    window.camera = (x: number, y: number, z: number) => {
        setCameraPosition(new Vector3(x, y, z))
    }

    useLayoutEffect(() => {
        // A zero position is the public sentinel for product-configured
        // framing. Explicit positions are preserved exactly for copied JSON.
        // cameraCommandId applies editor field/reset commands. Orbit feedback
        // deliberately leaves it unchanged so damping cannot feed back into
        // this effect. cameraRevision remains renderbot synchronization only.
        const requestedPosition = requestedCameraPosition.current
        const hasRequestedPosition = (
            requestedPosition.x !== 0 ||
            requestedPosition.y !== 0 ||
            requestedPosition.z !== 0
        )
        if(!hasRequestedPosition && props.deferDefaultCamera) return

        const nextPosition = hasRequestedPosition
            ? new Vector3(requestedPosition.x, requestedPosition.y, requestedPosition.z)
            : inEntryAnimation
                ? getEntryCameraPosition(defaultCameraPosition, entryCameraPosition, entryTime)
                : defaultCameraPosition.clone()
        const requestedTarget = requestedCameraTarget.current
        const nextTarget = new Vector3(
            requestedTarget.x,
            requestedTarget.y,
            requestedTarget.z,
        ).clampLength(0, maxPanDistance)
        const currentPosition = controls.current?.object.position
        const currentTarget = controls.current?.target
        const cameraNeedsUpdate = (
            !currentPosition
            || !currentTarget
            || !currentPosition.equals(nextPosition)
            || !currentTarget.equals(nextTarget)
        )

        const cameraRevision = props.cameraRevision
        const forceRenderSynchronization = cameraRevision !== undefined

        const applyRequestedView = () => {
            setCameraPosition(nextPosition)

            if (controls.current) {
                controls.current.object.position.copy(nextPosition)
                controls.current.target.copy(nextTarget)
                controls.current.update()
            }

            // PerspectiveCamera and OrbitControls normally share this object,
            // but makeDefault changes can briefly leave controls attached to
            // the previous R3F camera. Set the camera used by the renderer as
            // well so readiness never describes a different camera instance.
            if (controls.current?.object !== activeRenderCamera) {
                activeRenderCamera.position.copy(nextPosition)
                activeRenderCamera.lookAt(nextTarget)
                activeRenderCamera.updateMatrixWorld()
            }

            invalidate()
        }

        if(cameraNeedsUpdate || forceRenderSynchronization) {
            applyRequestedView()

            props.onCameraChange?.({
                position: {
                    x: nextPosition.x,
                    y: nextPosition.y,
                    z: nextPosition.z,
                },
                target: {
                    x: nextTarget.x,
                    y: nextTarget.y,
                    z: nextTarget.z,
                },
            })
        }

        if (cameraRevision === undefined) return
        renderReadiness.reset('camera', cameraRevision)

        // A generic RAF delay can acknowledge a stale OrbitControls target,
        // especially while makeDefault swaps cameras during a model change.
        // Force the requested view onto the active render camera and require
        // it to remain exact across rendered frames before releasing capture.
        const expectedDirection = nextTarget.clone().sub(nextPosition)
        if (expectedDirection.lengthSq() > 0) {
            expectedDirection.normalize()
        }
        const actualPosition = new Vector3()
        const actualDirection = new Vector3()
        let cameraReadyFrame = 0
        let cameraSyncFrames = 0
        let stableFrames = 0
        const synchronizeCamera = () => {
            applyRequestedView()
            cameraReadyFrame = requestAnimationFrame(() => {
                cameraSyncFrames++
                activeRenderCamera.getWorldPosition(actualPosition)
                activeRenderCamera.getWorldDirection(actualDirection)
                const targetMatches = (
                    controls.current === null
                    || controls.current.target.distanceToSquared(nextTarget)
                        <= CAMERA_SYNC_EPSILON_SQUARED
                )
                const cameraMatches = (
                    actualPosition.distanceToSquared(nextPosition)
                        <= CAMERA_SYNC_EPSILON_SQUARED
                    && (
                        expectedDirection.lengthSq() === 0
                        || actualDirection.distanceToSquared(expectedDirection)
                            <= CAMERA_SYNC_EPSILON_SQUARED
                    )
                    && targetMatches
                )
                stableFrames = cameraMatches ? stableFrames + 1 : 0

                if(stableFrames < CAMERA_SYNC_STABLE_FRAMES) {
                    if(cameraSyncFrames >= CAMERA_SYNC_MAX_FRAMES) {
                        renderReadiness.fail(
                            'Camera could not apply the requested position and target',
                        )
                        return
                    }
                    synchronizeCamera()
                    return
                }
                renderReadiness.acknowledge('camera', cameraRevision)
            })
        }
        synchronizeCamera()

        return () => {
            cancelAnimationFrame(cameraReadyFrame)
        }
    }, [
        activeRenderCamera,
        defaultCameraPosition,
        entryCameraPosition,
        entryTime,
        inEntryAnimation,
        invalidate,
        maxPanDistance,
        props.cameraCommandId,
        props.cameraRevision,
        props.deferDefaultCamera,
        props.model,
        props.onCameraChange,
        props.variant,
    ])

    useAnimFrame(inEntryAnimation, () => {
        if(!inEntryAnimation) return

        setCameraPosition(getEntryCameraPosition(
            defaultCameraPosition,
            entryCameraPosition,
            entryTime,
        ))
    }, [
        defaultCameraPosition,
        entryCameraPosition,
        entryTime,
        inEntryAnimation,
    ])

    useAnimFrame(inFixedViewTransition, () => {
        // Fixed-view buttons animate along the shortest azimuthal arc while
        // converging on the established product-view polar angle.
        const request = fixedViewRequest.current
        if (fixedView === 'custom' || request?.view !== fixedView) return

        const now = Date.now()
        const transitionTime = Math.max(0, Math.min(
            1,
            (now - request.startedAt) / FIXED_VIEW_TRANSITION_TIME_MS,
        ))
        const progress = Easing.Bezier(transitionTime)

        const angle = {
            'front': 0,
            'left': Math.PI/2,
            'right': 3*Math.PI/2,
            'back': Math.PI
        }[fixedView]

        if(!controls.current) return

        const polarDiff = (Math.PI / 2.32) - request.polar
        const azimuthalDiff = (angle - request.azimuthal + 3*Math.PI) % (2*Math.PI) - Math.PI
        // The easing curve owns the entire transition. Orbit damping would
        // make each step lag behind it and leave a jump at the final frame.
        const activeControls = controls.current
        const dampingEnabled = activeControls.enableDamping
        activeControls.enableDamping = false
        try {
            activeControls.setPolarAngle(request.polar + polarDiff * progress)
            activeControls.setAzimuthalAngle(request.azimuthal + azimuthalDiff * progress)
        } finally {
            activeControls.enableDamping = dampingEnabled
        }
        invalidate()
    }, [fixedView])

    useLayoutEffect(() => {
        if (fixedView === 'custom') {
            fixedViewRequest.current = null
            return
        }
        
        if (controls.current) {
            controls.current.setAzimuthalAngle(controls.current.getAzimuthalAngle())
            controls.current.setPolarAngle(controls.current.getPolarAngle())
        }

        const startedAt = Date.now()
        fixedViewRequest.current = controls.current ? {
            view: fixedView,
            startedAt,
            polar: controls.current.getPolarAngle(),
            azimuthal: controls.current.getAzimuthalAngle(),
        } : null
        setFixedViewTime(startedAt)
    }, [fixedView, setFixedViewTime])

    const onStart = useCallback(() => {
        fixedViewRequest.current = null
        setFixedView('custom')
        props.onControlsUsed?.()
    }, [props.onControlsUsed, setFixedView])

    const onChange = useCallback(() => {
        const activeControls = controls.current
        if(!activeControls) return

        const nextCameraTarget = activeControls.target.clone()
        const clampedCameraTarget = nextCameraTarget.clone().clampLength(0, maxPanDistance)
        if (!nextCameraTarget.equals(clampedCameraTarget)) {
            // Move camera and target by the same correction so hitting the pan
            // bound does not unexpectedly rotate or zoom the composition.
            const correction = clampedCameraTarget.clone().sub(nextCameraTarget)
            activeControls.target.copy(clampedCameraTarget)
            activeControls.object.position.add(correction)
            activeControls.object.updateMatrixWorld()
        }

        const nextCameraPosition = activeControls.object.getWorldPosition(new Vector3())
        const nextCameraDirection = activeControls.object.getWorldDirection(new Vector3())
        if(websiteMode && !gl.shadowMap.autoUpdate) {
            gl.shadowMap.needsUpdate = true
        }
        setCameraDirection((currentDirection) => {
            return currentDirection.equals(nextCameraDirection) ? currentDirection : nextCameraDirection
        })
        props.onCameraChange?.({
            position: {
                x: nextCameraPosition.x,
                y: nextCameraPosition.y,
                z: nextCameraPosition.z,
            },
            target: {
                x: clampedCameraTarget.x,
                y: clampedCameraTarget.y,
                z: clampedCameraTarget.z,
            },
        })
    }, [
        gl.shadowMap,
        maxPanDistance,
        props.onCameraChange,
        websiteMode,
    ])

    const cameraAnimating = inFixedViewTransition || inEntryAnimation

    return <>
        <Lights
            directionalShadows={directionalShadows}
            cameraDirection={cameraDirection}
            cycloramaBounceStrength={presentation.cyclorama.bounce_strength}
            cycloramaColor={presentation.cyclorama.color}
            cycloramaReflectionStrength={presentation.cyclorama.reflection_strength}
            presentationSetup={presentationSetup}
            studioAmbientLightIntensity={presentation.lights.ambient_intensity}
            studioDirectLightIntensity={presentation.lights.direct_intensity}
        />
        <PerspectiveCamera
            makeDefault
            fov={cameraFov}
            position={cameraPosition}
        />
        <OrbitControls
            // A configured selection change starts a new camera view. Remount
            // controls so damping momentum from the previous view cannot move
            // the camera away again after the layout reset.
            key={`${props.labData.id}:${props.variant}:${props.model ?? ''}`}
            ref={controls}
            enabled={interactive}
            enableDamping={interactive}
            enablePan={
                interactive
                && props.cameraPanEnabled === true
                && !cameraAnimating
            }
            enableZoom={interactive && !cameraAnimating}
            enableRotate={interactive && !cameraAnimating}
            minPolarAngle={minPolar}
            maxPolarAngle={maxPolar}
            minAzimuthAngle={minAzimuth}
            maxAzimuthAngle={maxAzimuth}
            minDistance={minDistance}
            maxDistance={maxDistance}
            dampingFactor={0.02}
            onChange={onChange}
            onStart={onStart}
            makeDefault
        />
    </>
}
